import { useMemo, type ReactElement } from 'react';
import { useSelector } from 'react-redux';
import { EmergencyCall } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCalls.types';
import {
  formatIngestionSource,
  useIsEditingResourceRequest,
} from '@rsos/reduxDataStore/src/features/resourceRequests';
import { getMostRecentLoc } from '@rsos/utils/getMostRecentLoc';
import { formatDateWithLocalTimezone } from '../../../utils/dateUtilities';
import { FullWidthContent } from '../../JurisdictionView/EventPane/tabs/OverviewTab/OverviewTab.styles';
import AirAmbulancePageHeader from '../AirAumbulancePage/AirAmbulancePageHeader';
import AirAmbulanceEtlEditForm from '../etl/AirAmbulanceEtlEditForm';
import AirAmbulanceEtlStatus from '../etl/AirAmbulanceEtlStatus';
import { useAirAmbulanceEtlEta, useAirAmbulanceEtlStatus } from '../etl/hooks';
import AirAmbulanceFormStandalone from './AirAmbulanceFormStandalone';
import { FORM_HEADER, STATUS_HEADER } from './constants';

interface EmergencyCallsState {
  emergencyCalls: {
    selectedCallerID: string | null;
    callQueue: Record<string, EmergencyCall>;
  };
}

interface CurrentHeaderState {
  text: string;
  subText?: string | null;
}

function getRequestMethod(
  verbatimMessage: Record<string, unknown> | null | undefined,
): string | null {
  const requestMethod = verbatimMessage?.requestMethod;

  if (typeof requestMethod !== 'string') {
    return null;
  }

  return requestMethod.trim().toLowerCase();
}

interface AirAmbulanceFormProps {
  fromMapCard?: boolean;
  hideBackButton?: boolean;
  noBorder?: boolean;
}

/**
 * Air Ambulance form. Uses Redux airAmbulanceSlice for ETL state
 * (tripId, transport) and the resource-request service.
 */
function AirAmbulanceForm({
  fromMapCard = false,
  hideBackButton = false,
  noBorder = false,
}: AirAmbulanceFormProps): JSX.Element {
  const { selectedCallerID, callQueue } = useSelector(
    (state: EmergencyCallsState) => state.emergencyCalls,
  );

  const isEditing = useIsEditingResourceRequest();

  // ETL status hook provides vehicle/provider/ETA info from ETL service
  const { transport, latestProvider, brandTail, selectedRequestId } =
    useAirAmbulanceEtlStatus();

  const hasLocationToRender = useMemo(
    () =>
      getMostRecentLoc(
        selectedCallerID && callQueue ? callQueue[selectedCallerID] : undefined,
      )
        ? true
        : false,
    [selectedCallerID, callQueue],
  );

  const eta = useAirAmbulanceEtlEta();

  const { currentHeader, currentPage } = useMemo((): {
    currentHeader: CurrentHeaderState;
    currentPage: ReactElement;
  } => {
    // For ETL, show edit form when we have a selectedRequestId and isEditing
    if (selectedRequestId && isEditing && transport) {
      return {
        currentHeader: {
          ...STATUS_HEADER,
          subText: [brandTail, latestProvider?.name]
            .filter(Boolean)
            .join(' - '),
        },
        currentPage: <AirAmbulanceEtlEditForm transport={transport} />,
      };
    }

    // For ETL, show status page when we have a selectedRequestId
    // TODO: we may need to add a "type" to these requests to support generic,
    // non-air-ambulance requests in the future and filter it here.
    if (transport) {
      return {
        currentHeader: {
          ...STATUS_HEADER,
          subText: [brandTail, latestProvider?.name]
            .filter(Boolean)
            .join(' - '),
        },
        currentPage: <AirAmbulanceEtlStatus transport={transport} />,
      };
    }

    // If we don't have a selected transport and we're not editing the
    // selected transport return this for creating a new transport
    return {
      currentHeader: {
        ...FORM_HEADER,
        subText: eta?.display,
      },
      currentPage: (
        <AirAmbulanceFormStandalone
          hasLocationToRender={hasLocationToRender}
          tentativeETA={eta}
          fromMapCard={fromMapCard}
        />
      ),
    };
  }, [
    hasLocationToRender,
    selectedRequestId,
    isEditing,
    transport,
    eta,
    brandTail,
    fromMapCard,
    latestProvider?.name,
  ]);

  const requestMethod = getRequestMethod(transport?.verbatim_message);

  const sourceLabel =
    selectedRequestId != null
      ? formatIngestionSource(transport?.ingestion_source, requestMethod)
      : null;

  const requestedAt = transport?.created_at
    ? formatDateWithLocalTimezone(transport.created_at, 'HH:mm')
    : null;

  return (
    <FullWidthContent backgroundColor="background" noBorder={noBorder}>
      <AirAmbulancePageHeader
        text={currentHeader?.text}
        subtext={currentHeader?.subText}
        hideBackButton={!selectedCallerID || hideBackButton}
        sourceLabel={sourceLabel}
        requestedAt={requestedAt}
      />
      {currentPage}
    </FullWidthContent>
  );
}

export default AirAmbulanceForm;
